import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Typography, message } from 'antd';

const FOCUS_MODE_STORAGE_KEY = 'reporting.focusMode';
const FOCUS_MODE_BODY_CLASS = 'report-focus-mode';
import { createTabChannel, getUserDetails, hasStudyViewingPermission, makePostCall } from '../../utils/helper';
import { launchViewerOnDualMonitor } from '../../utils/MonitorManager';
import type { OrderDetails } from './types';

import OOReportEditor from './components/Editor/OOReportEditor';

import './styles/reporting-page.css';

const { Text } = Typography;

/**
 * Top-level route component for `/oo-report`.
 *
 * Reads `order_ids` or `study_id` from search params, fetches order details,
 * and renders the study queue sidebar + report editor.
 */
const ReportingWorkspace: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [ordersList, setOrdersList] = useState<OrderDetails[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetails | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [focusMode, setFocusMode] = useState<boolean>(() => {
    // Default ON — only off when localStorage explicitly says 'false'
    try { return localStorage.getItem(FOCUS_MODE_STORAGE_KEY) !== 'false'; }
    catch { return true; }
  });

  const toggleFocusMode = useCallback(() => {
    setFocusMode(prev => {
      const next = !prev;
      try { localStorage.setItem(FOCUS_MODE_STORAGE_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  // Apply body class so global header (rendered outside this route) can be hidden via CSS
  useEffect(() => {
    if (focusMode) document.body.classList.add(FOCUS_MODE_BODY_CLASS);
    else document.body.classList.remove(FOCUS_MODE_BODY_CLASS);
    return () => document.body.classList.remove(FOCUS_MODE_BODY_CLASS);
  }, [focusMode]);

  // Keyboard shortcut: Ctrl+Shift+F
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'F' || e.key === 'f')) {
        e.preventDefault();
        toggleFocusMode();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggleFocusMode]);

  const order_ids = searchParams.get('order_ids');
  const study_id = searchParams.get('study_id');
  const channel = createTabChannel();

  useEffect(() => () => channel.cleanup(), []);

  useEffect(() => {
    if (order_ids?.length) fetchOrderDetails(order_ids, null);
    else if (study_id) fetchOrderDetails('', study_id);
  }, [order_ids, study_id]);

  const fetchOrderDetails = async (orderIds: string, studyId: string | null) => {
    setLoading(true);
    try {
      const resp = await makePostCall('/orders-list-details', {
        order_ids: orderIds === '' ? [] : orderIds.split(','),
        study_uid: studyId,
        user_id: getUserDetails()?.username,
      });

      const ordData = resp?.data;
      [...(ordData.conflictingStudies || []), ...(ordData.reportingDisabledStudies || []), ...(ordData.signedOffStudies || [])].forEach(itm => {
        const reason = itm.opened_by ? `This study is being reported by ${itm.opened_by}.` : itm.reason || 'reporting disabled';
        message.warning(`${itm.study} - ${reason}`, 10);
      });

      setOrdersList(ordData?.data || []);

      if (!(ordData?.data?.length > 0)) {
        message.info('No studies available for reporting');
        return;
      }

      handleOrderSelect(ordData.data[0], true, -1);
      if (!studyId && orderIds !== '' && hasStudyViewingPermission(getUserDetails())) {
        launchViewerOnDualMonitor(ordData.data[0]?.ps_study_uid, ordData.data[0]);
      }
    } catch {
      message.error('Failed to fetch order details');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderSelect = useCallback(async (order: OrderDetails, isInitial: boolean, index: number) => {
    const response = await makePostCall('/study-report-details', {
      user_id: getUserDetails()?.username,
      order_id: order?.pacs_order?.pacs_ord_id,
    });

    if (response?.data?.success) {
      const detail = response.data.data;
      setSelectedOrder({
        ...order,
        ow_diagnosed: detail?.ow_diagnosed ?? null,
        ow_correlated: detail?.ow_correlated ?? null,
      });
      setSelectedIndex(index);
    } else {
      if (response?.data?.limitReached) {
        const held = response.data.heldStudies || [];
        message.warning({
          content: (
            <div>
              <div>{response.data.message}</div>
              {held.length > 0 && (
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {held.map((s: string, i: number) => <li key={i}>{s}</li>)}
                </ul>
              )}
            </div>
          ),
          duration: 10,
        });
      } else {
        message.error(response?.data?.message);
      }
      setSelectedOrder(null);
      setSelectedIndex(-1);
      return;
    }

    if (!isInitial && hasStudyViewingPermission(getUserDetails())) {
      // channel.sendUrlUpdate(`/viewer/viewer?StudyInstanceUIDs=${order?.ps_study_uid}`);
    } else {
      setSelectedIndex(0);
    }
  }, []);

  const handlePostSaving = useCallback((_status: string, _patDetails: OrderDetails) => {
    if (ordersList.length <= 1) {
      message.info('Closing the window..');
      setTimeout(() => { window.open('about:blank', '_self'); window.close(); }, 2000);
      return;
    }

    const remaining = ordersList.filter((_, i) => i !== selectedIndex);
    const nextIndex = selectedIndex >= remaining.length ? remaining.length - 1 : selectedIndex;

    message.info('Moving to next study..');
    setTimeout(() => {
      setOrdersList(remaining);
      setSelectedOrder(remaining[nextIndex]);
      setSelectedIndex(nextIndex);
    }, 1000);
  }, [selectedIndex, ordersList]);

  return (
    <div className={`reporting-page-layout${focusMode ? ' focus-mode' : ''}`}>
      <div className="content-layout">
        <div className="content-wrapper">
          {ordersList.length > 0 ? (
            <OOReportEditor
              patientDetails={selectedOrder}
              orders={ordersList}
              selectedIndex={selectedIndex}
              queueLoading={loading}
              onSelectOrder={(order, idx) => handleOrderSelect(order, false, idx)}
              handlePostSaving={handlePostSaving}
              cancel={() => { window.open('about:blank', '_self'); window.close(); }}
              focusMode={focusMode}
              onToggleFocusMode={toggleFocusMode}
            />
          ) : (
            <div className="empty-state">
              <Text type="secondary">{loading ? 'Loading studies…' : 'No studies available for reporting'}</Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportingWorkspace;
